import React, { useEffect, useRef } from 'react';
import { Mail, Phone } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SocialLinks from './SocialLinks';

gsap.registerPlugin(ScrollTrigger);

const Contact: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(contentRef.current, {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
          end: 'top 50%',
          scrub: 1,
        },
        opacity: 0,
        y: 50,
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="contact" ref={sectionRef} className="min-h-[60vh] py-24 px-6 md:px-12 lg:px-24 flex items-center">
      <div ref={contentRef} className="max-w-4xl mx-auto w-full text-center">
        <h2 className="font-mono text-sm text-accent uppercase tracking-[0.3em] mb-8">
          {'// GET IN TOUCH'}
        </h2>

        <h3 className="text-3xl md:text-5xl font-bold mb-6">
          Let&rsquo;s <span className="text-gradient">build something</span> together
        </h3>

        <p className="text-gray-400 text-lg mb-12 max-w-2xl mx-auto">
          I&rsquo;m always open to discussing new opportunities, technical challenges, or collaborations. Whether you need a seasoned engineer for your team or a technical consultant for your next project, let&rsquo;s connect.
        </p>

        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
          <a
            href="mailto:brandonellis.email@gmail.com?subject=Website Inquiry"
            className="group flex items-center gap-3 px-6 py-4 rounded-xl border border-white/10 hover:border-accent/50 hover:shadow-neon bg-white/5 transition-all duration-300"
          >
            <Mail className="text-accent group-hover:animate-pulse" size={20} />
            <span className="text-gray-300 group-hover:text-white transition-colors">brandonellis.email@gmail.com</span>
          </a>

          <a
            href="tel:+15183543096"
            className="group flex items-center gap-3 px-6 py-4 rounded-xl border border-white/10 hover:border-accent/50 hover:shadow-neon bg-white/5 transition-all duration-300"
          >
            <Phone className="text-accent group-hover:animate-pulse" size={20} />
            <span className="text-gray-300 group-hover:text-white transition-colors">+1 (518) 354-3096</span>
          </a>
        </div>

        <SocialLinks size={22} className="justify-center" />

        {/* Footer */}
        <div className="mt-20 pt-8 border-t border-white/5">
          <p className="text-gray-600 text-sm font-mono">
            &copy; {new Date().getFullYear()} Brandon T. Ellis | Built with React + TypeScript
          </p>
        </div>
      </div>
    </section>
  );
};

export default Contact;
